<template>
  <div 
    ref="editorContainer"
    class="w-full h-full relative overflow-hidden bg-background"
    @wheel.prevent="handleWheel"
    @mousedown="handleEditorMouseDown"
    @mousemove="handleEditorMouseMove"
    @mouseup="handleEditorMouseUp"
    @mouseleave="handleEditorMouseUp"
  >
    <!-- Display Name -->
    <div class="absolute top-2 left-4 z-[100]">
      <div class="text-sm text-foreground font-medium bg-card px-3 py-1 shadow-sm border border-border rounded">
        {{ display.name }} ({{ display.width }}×{{ display.height }})
      </div>
    </div>

    <!-- Zoom Controls - matches SceneGraphView style -->
    <div class="absolute bottom-4 left-4 flex flex-col gap-2 z-[100]">
      <Button
        variant="outline"
        size="icon"
        @click="zoomIn"
        title="Zoom In"
        class="bg-background shadow-lg"
      >
        <Plus class="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        @click="zoomOut"
        title="Zoom Out"
        class="bg-background shadow-lg"
      >
        <Minus class="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        @click="fitToView"
        title="Fit View"
        class="bg-background shadow-lg"
      >
        <Maximize2 class="h-4 w-4" />
      </Button>
    </div>

    <!-- Pan hint -->
    <div 
      v-if="isPanning"
      class="absolute top-2 right-4 text-xs text-muted-foreground bg-card px-2 py-1 shadow-sm border border-border rounded z-[100]"
    >
      Panning...
    </div>

    <!-- Canvas Container (viewport) -->
    <div 
      class="w-full h-full overflow-hidden select-none"
      :class="{ 'cursor-grab': !isPanning && !dragging && isSpacePressed, 'cursor-grabbing': isPanning }"
    >
      <!-- Transform layer for pan/zoom -->
      <div 
        class="w-full h-full flex items-center justify-center"
        :style="{ 
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${userZoom})`,
          transformOrigin: 'center center'
        }"
      >
        <div 
          ref="canvasContainer"
          class="relative bg-card border-2 border-dashed border-border shadow-inner overflow-visible"
          :style="{ 
            width: `${scaledCanvasWidth}px`, 
            height: `${scaledCanvasHeight}px`
          }"
          @click="handleCanvasClick"
        >
        <!-- Canvas Background -->
        <div class="absolute inset-0 bg-black">
        </div>

        <!-- Display Bounds Outline -->
        <div class="absolute inset-0 border-2 border-blue-400 border-dashed pointer-events-none z-50"></div>

        <!-- Layers -->
        <div 
                  v-for="layer in sortedLayers" 
                  v-show="layer.visible"
                  :key="layer.id"
                  class="absolute border-2 cursor-move"
          :class="{
            'border-blue-500 shadow-lg': selectedLayerId === layer.id && !layer.is_inherited,
            'border-orange-500 shadow-lg shadow-orange-500/50': selectedLayerId === layer.id && layer.is_inherited,
            'border-transparent hover:border-gray-400': selectedLayerId !== layer.id && !layer.is_inherited,
            'border-orange-300/50 hover:border-orange-400': selectedLayerId !== layer.id && layer.is_inherited,
            'ring-2 ring-orange-400/30': layer.is_inherited
          }"
          :style="{
            left: `${layer.x * scale}px`,
            top: `${layer.y * scale}px`,
            width: `${layer.width * scale}px`,
            height: `${layer.height * scale}px`,
            transform: `rotate(${layer.effects.rotation || 0}deg)`,
            opacity: layer.effects.opacity || 1,
            mixBlendMode: layer.effects.blendMode || 'normal',
            zIndex: layer.zIndex + 10,
            willChange: 'left, top, width, height, transform',
            isolation: 'isolate'
          } as any"
          @click.stop="handleLayerClick(layer, $event)"
          @mousedown="handleMouseDown($event, layer)"
        >
          <!-- Inherited Layer Badge -->
          <div 
            v-if="layer.is_inherited" 
            class="absolute -top-6 left-0 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-t z-50 whitespace-nowrap shadow-sm"
            style="user-select: none; pointer-events: none;"
          >
            {{ layer.parent_scene_name || 'Parent' }}
          </div>
          <div
            v-if="getCropOverlayStyle(layer)"
            class="absolute inset-0 pointer-events-none"
            style="z-index: 30;"
          >
            <div
              class="absolute rounded-sm border border-yellow-400/80 border-dashed bg-black/10"
              style="z-index: 31;"
              :style="getCropOverlayStyle(layer)"
            ></div>
          </div>
          <!-- Image Layer -->
          <div v-if="layer.type === 'image'" class="w-full h-full relative overflow-hidden">
            <img 
              :src="getLayerImageUrl(layer)" 
              :alt="layer.name"
              class="w-full h-full"
              :class="`object-${layer.objectFit || 'cover'}`"
              :style="{
                filter: getFilterStyles(layer),
                clipPath: getCropStyles(layer)
              }"
              @error="handleImageError"
              draggable="false"
            />
            <!-- Tint Overlay -->
            <div 
              v-if="layer.effects.tint" 
              class="absolute inset-0 pointer-events-none"
              :style="{
                backgroundColor: layer.effects.tint,
                mixBlendMode: 'multiply',
                opacity: 0.5
              }"
            ></div>
            <div class="absolute inset-0 bg-blue-500/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
              <span class="text-xs text-white font-medium">{{ layer.name }}</span>
            </div>
          </div>

          <!-- YouTube Video Layer -->
          <div v-else-if="layer.type === 'youtube'" class="w-full h-full relative overflow-hidden">
            <iframe 
              :src="`${getYouTubeEmbedUrl(layer.content)}${layer.content.includes('?') ? '&' : '?'}autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&showinfo=0`"
              class="w-full h-full rounded border-0 pointer-events-none"
              :style="{
                filter: getFilterStyles(layer),
                clipPath: getCropStyles(layer),
                aspectRatio: '16/9'
              }"
              allowfullscreen
            ></iframe>
            <!-- Tint Overlay -->
            <div 
              v-if="layer.effects.tint" 
              class="absolute inset-0 pointer-events-none rounded"
              :style="{
                backgroundColor: layer.effects.tint,
                mixBlendMode: 'multiply',
                opacity: 0.5
              }"
            ></div>
          </div>

          <!-- Video Layer -->
          <video
            v-else-if="layer.type === 'video'"
            class="w-full h-full rounded pointer-events-none"
            :class="`object-${layer.objectFit || 'cover'}`"
            :src="getLayerImageUrl(layer)"
            :style="{
              filter: getFilterStyles(layer),
              clipPath: getCropStyles(layer)
            }"
            autoplay
            loop
            muted
            playsinline
          />

          <!-- Text Layer -->
          <div v-else-if="layer.type === 'text'" class="w-full h-full bg-blue-50 border border-blue-300 rounded flex items-center justify-center">
            <span class="text-sm font-medium text-blue-800 truncate px-2">{{ layer.content || 'Text Layer' }}</span>
          </div>
        </div>

        <!-- Selected Layer Handles - rendered above all layers -->
        <div 
          v-if="selectedLayer && !selectedLayer.locked"
          class="absolute pointer-events-none"
          :style="{
            left: `${selectedLayer.x * scale}px`,
            top: `${selectedLayer.y * scale}px`,
            width: `${selectedLayer.width * scale}px`,
            height: `${selectedLayer.height * scale}px`,
            transform: `rotate(${selectedLayer.effects?.rotation || 0}deg)`,
            zIndex: 9999
          }"
        >
          <!-- Corner resize handles -->
          <div class="absolute -top-1 -left-1 w-3 h-3 bg-blue-500 border border-white rounded-full cursor-nw-resize pointer-events-auto" @mousedown.stop="startResize($event, selectedLayer, 'nw')"></div>
          <div class="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 border border-white rounded-full cursor-ne-resize pointer-events-auto" @mousedown.stop="startResize($event, selectedLayer, 'ne')"></div>
          <div class="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-500 border border-white rounded-full cursor-sw-resize pointer-events-auto" @mousedown.stop="startResize($event, selectedLayer, 'sw')"></div>
          <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 border border-white rounded-full cursor-se-resize pointer-events-auto" @mousedown.stop="startResize($event, selectedLayer, 'se')"></div>
          
          <!-- Crop handles -->
          <div class="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-2 bg-yellow-500 border border-white cursor-ns-resize pointer-events-auto" @mousedown.stop="startCrop($event, selectedLayer, 'top')" title="Crop Top"></div>
          <div class="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-2 bg-yellow-500 border border-white cursor-ns-resize pointer-events-auto" @mousedown.stop="startCrop($event, selectedLayer, 'bottom')" title="Crop Bottom"></div>
          <div class="absolute -left-2 top-1/2 transform -translate-y-1/2 w-2 h-4 bg-yellow-500 border border-white cursor-ew-resize pointer-events-auto" @mousedown.stop="startCrop($event, selectedLayer, 'left')" title="Crop Left"></div>
          <div class="absolute -right-2 top-1/2 transform -translate-y-1/2 w-2 h-4 bg-yellow-500 border border-white cursor-ew-resize pointer-events-auto" @mousedown.stop="startCrop($event, selectedLayer, 'right')" title="Crop Right"></div>
        </div>

        <!-- Snap Guide Lines - centered on the snap position -->
        <div 
          v-for="guide in activeSnapGuides" 
          :key="`${guide.type}-${guide.position}`"
          class="absolute pointer-events-none z-[100]"
          :class="guide.type === 'vertical' ? 'w-0.5 h-full bg-pink-500' : 'h-0.5 w-full bg-pink-500'"
          :style="guide.type === 'vertical' 
            ? { left: `${guide.position * scale - 1}px`, top: 0 }
            : { top: `${guide.position * scale - 1}px`, left: 0 }"
        ></div>

        <!-- Empty State -->
        <div v-if="layers.length === 0" class="absolute inset-0 flex items-center justify-center">
          <div class="text-center text-gray-500">
            <svg class="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <p class="text-sm font-medium mb-1">No layers yet</p>
            <p class="text-xs text-gray-400">Drag images or videos from the library</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import type { Display } from '@/stores/displays'
import type { DisplayLayer } from '@/stores/sceneDisplayConfigs'
import { useImageCacheStore } from '@/stores/imageCache'
import { useLayerStyles } from '@/composables/useLayerStyles'
import { Button } from '@/components/ui/button'
import { Plus, Minus, Maximize2 } from 'lucide-vue-next'
import { logger } from '@/lib/logger'
import '@/assets/layer-animations.css'

interface Props {
  display: Display
  layers: DisplayLayer[]
  selectedLayerId: string | null
}

const props = defineProps<Props>()

interface LayerUpdateOptions {
  commit?: boolean
  deferCommit?: boolean
  skipBroadcast?: boolean
  throttle?: boolean
}

const emit = defineEmits<{
  updateLayer: [layer: DisplayLayer, options?: LayerUpdateOptions]
  selectLayer: [layerId: string]
  deleteLayer: [layerId: string]
  addLayer: [imageUrl: string, imageName: string]
}>()

// Use shared layer styles
const {
  getFilterStyles: getBaseFilterStyles,
  getCropStyles,
  getCropOverlayStyle
} = useLayerStyles()

// Image caching - use global store
const imageCacheStore = useImageCacheStore()
const { cacheVersion } = storeToRefs(imageCacheStore)

// Track retries per layer to prevent infinite loops
const layerRetryCount = new Map<string, number>()
const MAX_LAYER_RETRIES = 2

// Preload images when layers change - use storage paths for efficient caching
watch(() => props.layers, async (newLayers) => {
  if (!newLayers || newLayers.length === 0) return
  
  const storagePaths = newLayers
    .filter(layer => layer.type === 'image' && layer.storage_path)
    .map(layer => layer.storage_path!)
  
  if (storagePaths.length > 0) {
    await imageCacheStore.preloadImagesByPath(storagePaths, 'user-images')
  }
}, { immediate: true, deep: true })

// Computed map of layer IDs to image URLs - properly tracks cache reactivity
const layerImageUrls = computed(() => {
  // Access cacheVersion.value to create reactive dependency - this is the key!
  // When cacheVersion changes, this entire computed re-runs
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  cacheVersion.value
  
  const urlMap = new Map<string, string>()
  
  for (const layer of props.layers) {
    if (layer.type !== 'image') {
      urlMap.set(layer.id, layer.content)
      continue
    }
    
    // Use storage_path for cache-first loading
    if (layer.storage_path) {
      const cachedUrl = imageCacheStore.getCachedUrlByPath(layer.storage_path)
      if (cachedUrl) {
        urlMap.set(layer.id, cachedUrl)
      } else {
        // Fallback to content URL while loading
        urlMap.set(layer.id, layer.content || '')
      }
    } else {
      // Fallback to content URL for backwards compatibility
      const cachedUrl = imageCacheStore.getCachedUrl(layer.content)
      urlMap.set(layer.id, cachedUrl || layer.content)
    }
  }
  
  return urlMap
})

// Get cached image URL for a layer - uses the reactive computed map
const getLayerImageUrl = (layer: DisplayLayer): string => {
  return layerImageUrls.value.get(layer.id) || layer.content || ''
}

// Refs
const editorContainer = ref<HTMLElement>()
const canvasContainer = ref<HTMLElement>()
const dragging = ref(false)
const draggedLayer = ref<DisplayLayer | null>(null)
const resizing = ref(false)

// Snapping state
const SNAP_THRESHOLD = 8 // pixels in canvas space
const isAltPressed = ref(false) // Alt disables snapping
const activeSnapGuides = ref<{ type: 'vertical' | 'horizontal'; position: number }[]>([])

// Snapping helper types
interface SnapResult {
  value: number
  snapped: boolean
  guides: { type: 'vertical' | 'horizontal'; position: number }[]
}

// Get all snap targets for a given axis
const getSnapTargets = (axis: 'x' | 'y', excludeLayerId?: string): number[] => {
  const targets: number[] = []
  const canvasSize = axis === 'x' ? props.display.width : props.display.height
  
  // Canvas edges
  targets.push(0) // Left/Top edge
  targets.push(canvasSize) // Right/Bottom edge
  targets.push(canvasSize / 2) // Center
  
  return targets
}

// Try to snap a value to nearby targets
const snapToTargets = (value: number, targets: number[], axis: 'x' | 'y'): SnapResult => {
  if (isAltPressed.value) {
    return { value, snapped: false, guides: [] }
  }
  
  for (const target of targets) {
    if (Math.abs(value - target) <= SNAP_THRESHOLD) {
      return {
        value: target,
        snapped: true,
        guides: [{ type: axis === 'x' ? 'vertical' : 'horizontal', position: target }]
      }
    }
  }
  
  return { value, snapped: false, guides: [] }
}

// Snap layer position (checks left, right, center of layer against targets)
const snapLayerPosition = (
  x: number, 
  y: number, 
  width: number, 
  height: number, 
  excludeLayerId?: string
): { x: number; y: number; guides: { type: 'vertical' | 'horizontal'; position: number }[] } => {
  const guides: { type: 'vertical' | 'horizontal'; position: number }[] = []
  let snappedX = x
  let snappedY = y
  
  const xTargets = getSnapTargets('x', excludeLayerId)
  const yTargets = getSnapTargets('y', excludeLayerId)
  
  // Try snapping left edge
  let result = snapToTargets(x, xTargets, 'x')
  if (result.snapped) {
    snappedX = result.value
    guides.push(...result.guides)
  } else {
    // Try snapping right edge
    result = snapToTargets(x + width, xTargets, 'x')
    if (result.snapped) {
      snappedX = result.value - width
      guides.push(...result.guides)
    } else {
      // Try snapping center
      result = snapToTargets(x + width / 2, xTargets, 'x')
      if (result.snapped) {
        snappedX = result.value - width / 2
        guides.push(...result.guides)
      }
    }
  }
  
  // Try snapping top edge
  result = snapToTargets(y, yTargets, 'y')
  if (result.snapped) {
    snappedY = result.value
    guides.push(...result.guides)
  } else {
    // Try snapping bottom edge
    result = snapToTargets(y + height, yTargets, 'y')
    if (result.snapped) {
      snappedY = result.value - height
      guides.push(...result.guides)
    } else {
      // Try snapping center
      result = snapToTargets(y + height / 2, yTargets, 'y')
      if (result.snapped) {
        snappedY = result.value - height / 2
        guides.push(...result.guides)
      }
    }
  }
  
  return { x: snappedX, y: snappedY, guides }
}

// Snap during resize (snap the edge being resized)
const snapResize = (
  edge: 'left' | 'right' | 'top' | 'bottom',
  value: number,
  excludeLayerId?: string
): SnapResult => {
  const axis = (edge === 'left' || edge === 'right') ? 'x' : 'y'
  const targets = getSnapTargets(axis, excludeLayerId)
  return snapToTargets(value, targets, axis)
}

// Zoom and Pan state
const userZoom = ref(1)
const panOffset = ref({ x: 0, y: 0 })
const isPanning = ref(false)
const panStart = ref({ x: 0, y: 0 })
const panStartOffset = ref({ x: 0, y: 0 })
const isSpacePressed = ref(false)

// Zoom limits
const MIN_ZOOM = 0.1
const MAX_ZOOM = 5
const ZOOM_STEP = 0.1

// Zoom functions
const zoomIn = () => {
  userZoom.value = Math.min(MAX_ZOOM, userZoom.value + ZOOM_STEP)
}

const zoomOut = () => {
  userZoom.value = Math.max(MIN_ZOOM, userZoom.value - ZOOM_STEP)
}

const resetZoom = () => {
  userZoom.value = 1
  panOffset.value = { x: 0, y: 0 }
}

const fitToView = () => {
  if (!editorContainer.value) return
  
  const containerRect = editorContainer.value.getBoundingClientRect()
  const padding = 80 // Account for UI elements
  
  const availableWidth = containerRect.width - padding
  const availableHeight = containerRect.height - padding
  
  const fitZoomX = availableWidth / scaledCanvasWidth.value
  const fitZoomY = availableHeight / scaledCanvasHeight.value
  
  userZoom.value = Math.min(fitZoomX, fitZoomY, 2) // Cap at 200%
  panOffset.value = { x: 0, y: 0 }
}

// Mouse wheel handler - matches SceneGraphView navigation
const handleWheel = (e: WheelEvent) => {
  // Shift + scroll = pan horizontally (check first before zoom modifiers)
  if (e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
    const panDelta = e.deltaY * 0.5
    panOffset.value = {
      x: panOffset.value.x - panDelta,
      y: panOffset.value.y
    }
  } else if (e.altKey || e.ctrlKey || e.metaKey) {
    // Alt, Ctrl, or Cmd + scroll = zoom toward cursor
    const zoomDelta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, userZoom.value + zoomDelta))
    
    // Zoom toward mouse position
    if (editorContainer.value && newZoom !== userZoom.value) {
      const rect = editorContainer.value.getBoundingClientRect()
      const mouseX = e.clientX - rect.left - rect.width / 2
      const mouseY = e.clientY - rect.top - rect.height / 2
      
      const zoomFactor = newZoom / userZoom.value
      
      // Adjust pan to zoom toward mouse
      panOffset.value = {
        x: mouseX - (mouseX - panOffset.value.x) * zoomFactor,
        y: mouseY - (mouseY - panOffset.value.y) * zoomFactor
      }
      
      userZoom.value = newZoom
    }
  } else {
    // Normal scroll = pan vertically
    const panDelta = e.deltaY * 0.5
    panOffset.value = {
      x: panOffset.value.x,
      y: panOffset.value.y - panDelta
    }
  }
}

// Pan handlers
const handleEditorMouseDown = (e: MouseEvent) => {
  // Middle mouse button or space + left click for panning
  if (e.button === 1 || (e.button === 0 && isSpacePressed.value)) {
    e.preventDefault()
    isPanning.value = true
    panStart.value = { x: e.clientX, y: e.clientY }
    panStartOffset.value = { ...panOffset.value }
  }
}

const handleEditorMouseMove = (e: MouseEvent) => {
  if (isPanning.value) {
    const deltaX = e.clientX - panStart.value.x
    const deltaY = e.clientY - panStart.value.y
    panOffset.value = {
      x: panStartOffset.value.x + deltaX,
      y: panStartOffset.value.y + deltaY
    }
  }
}

const handleEditorMouseUp = () => {
  isPanning.value = false
}

// Space key for pan mode
const handleKeyDownForPan = (e: KeyboardEvent) => {
  if (e.code === 'Space' && !e.repeat) {
    const target = e.target as HTMLElement
    if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
      e.preventDefault()
      isSpacePressed.value = true
    }
  }
}

const handleKeyUpForPan = (e: KeyboardEvent) => {
  if (e.code === 'Space') {
    isSpacePressed.value = false
  }
}

// Canvas sizing
const maxCanvasWidth = 800
const maxCanvasHeight = 600

const canvasWidth = computed(() => props.display.width)
const canvasHeight = computed(() => props.display.height)

const scale = computed(() => {
  if (!props.display.width || !props.display.height) {
    return 1
  }
  const scaleX = maxCanvasWidth / props.display.width
  const scaleY = maxCanvasHeight / props.display.height
  return Math.min(scaleX, scaleY, 1)
})

// Total scale including user zoom - used for all mouse interaction calculations
const totalScale = computed(() => scale.value * userZoom.value)

const scaledCanvasWidth = computed(() => props.display.width * scale.value)
const scaledCanvasHeight = computed(() => props.display.height * scale.value)

// Convert YouTube embed URL to nocookie variant for Tauri compatibility
const getYouTubeEmbedUrl = (content: string): string => {
  return content.replace('youtube.com', 'youtube-nocookie.com')
}

// Wrapper to apply scale factor to filter styles (for blur)
const getFilterStyles = (layer: DisplayLayer) => {
  return getBaseFilterStyles(layer, scale.value)
}

// Sorted layers by z-index (highest first for proper rendering)
const sortedLayers = computed(() => {
  if (!Array.isArray(props.layers)) return []
  return [...props.layers]
    .filter(layer => layer && layer.id) // Filter out any null/undefined layers
    .sort((a, b) => b.zIndex - a.zIndex)
})

// Get the currently selected layer
const selectedLayer = computed(() => {
  if (!props.selectedLayerId) return null
  return props.layers.find(layer => layer.id === props.selectedLayerId) || null
})

// Track if multiple layers are at cursor position
const layersAtCursor = ref<DisplayLayer[]>([])
const showLayerStack = ref(false)

// Click-through cycling state
let lastClickPos = { x: 0, y: 0 }
let lastClickTime = 0
const CLICK_CYCLE_THRESHOLD = 500 // ms

// Helper to get all layers at a point (in z-order, highest first)
const getLayersAtPoint = (clientX: number, clientY: number): DisplayLayer[] => {
  if (!canvasContainer.value) return []
  
  const rect = canvasContainer.value.getBoundingClientRect()
  const canvasX = (clientX - rect.left) / totalScale.value
  const canvasY = (clientY - rect.top) / totalScale.value
  
  return sortedLayers.value.filter(layer => {
    // Skip locked layers
    if (layer.locked) return false
    
    const layerLeft = layer.x
    const layerTop = layer.y
    const layerRight = layer.x + layer.width
    const layerBottom = layer.y + layer.height
    
    return canvasX >= layerLeft && canvasX <= layerRight &&
           canvasY >= layerTop && canvasY <= layerBottom
  })
}

// Event handlers
const handleCanvasClick = (e: MouseEvent) => {
  if (e.target === canvasContainer.value) {
    emit('selectLayer', '')
  }
}

const handleLayerClick = (layer: DisplayLayer, event: MouseEvent) => {
  // Skip locked layers entirely
  if (layer.locked) {
    return
  }
  
  const now = Date.now()
  const clickPos = { x: event.clientX, y: event.clientY }
  
  // Check if clicking same spot within threshold
  const isSameSpot = 
    Math.abs(clickPos.x - lastClickPos.x) < 5 &&
    Math.abs(clickPos.y - lastClickPos.y) < 5 &&
    (now - lastClickTime) < CLICK_CYCLE_THRESHOLD
  
  if (isSameSpot && props.selectedLayerId === layer.id) {
    // Cycle to next layer at this position
    const layersAtPoint = getLayersAtPoint(event.clientX, event.clientY)
    
    if (layersAtPoint.length > 1) {
      const currentIndex = layersAtPoint.findIndex(l => l.id === layer.id)
      const nextLayer = layersAtPoint[(currentIndex + 1) % layersAtPoint.length]
      emit('selectLayer', nextLayer.id)
      
      console.log('🔄 Cycling through layers:', {
        total: layersAtPoint.length,
        from: layer.name,
        to: nextLayer.name,
        availableLayers: layersAtPoint.map(l => l.name)
      })
    }
  } else {
    // Normal selection
    emit('selectLayer', layer.id)
  }
  
  lastClickPos = clickPos
  lastClickTime = now
}

const handleImageError = async (e: Event) => {
  const img = e.target as HTMLImageElement
  const originalSrc = img.src
  
  // Show placeholder immediately
  img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBFcnJvcjwvdGV4dD4KPHN2Zz4='
  
  // If this was a blob URL that failed, invalidate the cache entry and retry
  if (originalSrc.startsWith('blob:')) {
    // Find which layer this was and retry loading
    for (const layer of props.layers) {
      if (layer.type !== 'image') continue
      const cachedUrl = layerImageUrls.value.get(layer.id)
      if (cachedUrl === originalSrc && layer.storage_path) {
        // Check retry count to prevent infinite loops (Firefox security error issue)
        const retryCount = layerRetryCount.get(layer.id) || 0
        if (retryCount >= MAX_LAYER_RETRIES) {
          logger.warn('[CanvasEditor] Max retries reached for layer:', layer.id)
          break
        }
        
        layerRetryCount.set(layer.id, retryCount + 1)
        logger.warn(`[CanvasEditor] Blob URL failed, retry ${retryCount + 1}/${MAX_LAYER_RETRIES}:`, layer.id)
        
        // Invalidate the cached entry
        await imageCacheStore.invalidateByPath(layer.storage_path)
        // Re-fetch the image
        const newUrl = await imageCacheStore.getImageByPath(layer.storage_path, 'user-images')
        if (newUrl && newUrl !== originalSrc) {
          img.src = newUrl
        }
        break
      }
    }
  }
}

// Handle mousedown - use selected layer if already selected, otherwise use the clicked layer
const handleMouseDown = (e: MouseEvent, clickedLayer: DisplayLayer) => {
  // If this layer is already selected, use it for dragging
  if (props.selectedLayerId === clickedLayer.id) {
    startDrag(e, clickedLayer)
  } else {
    // If a different layer is selected at this position, use the selected one
    const layersAtPoint = getLayersAtPoint(e.clientX, e.clientY)
    const selectedLayer = layersAtPoint.find(l => l.id === props.selectedLayerId)
    
    if (selectedLayer) {
      // Drag the currently selected layer instead
      startDrag(e, selectedLayer)
    } else {
      // No selected layer at this point, use the clicked layer
      startDrag(e, clickedLayer)
    }
  }
}

// Drag and drop handlers
let startPos = { x: 0, y: 0 }
let startSize = { width: 0, height: 0 }

const startDrag = (e: MouseEvent, layer: DisplayLayer) => {
  // Prevent dragging locked layers
  if (layer.locked) {
    return
  }
  
  dragging.value = true
  draggedLayer.value = layer
  
  const rect = canvasContainer.value?.getBoundingClientRect()
  if (rect) {
    startPos = {
      x: e.clientX - rect.left - layer.x * totalScale.value,
      y: e.clientY - rect.top - layer.y * totalScale.value
    }
  }
  
  document.addEventListener('mousemove', handleDragMove)
  document.addEventListener('mouseup', endDrag)
  e.preventDefault()
}

let dragRafId: number | null = null
let pendingDragUpdate: { x: number; y: number } | null = null

const handleDragMove = (e: MouseEvent) => {
  if (!dragging.value || !draggedLayer.value || !canvasContainer.value) return
  
  const rect = canvasContainer.value.getBoundingClientRect()
  // Allow unlimited movement - no boundary constraints, round to pixels
  let newX = Math.round((e.clientX - rect.left - startPos.x) / totalScale.value)
  let newY = Math.round((e.clientY - rect.top - startPos.y) / totalScale.value)

  // Apply snapping
  const snapResult = snapLayerPosition(
    newX, 
    newY, 
    draggedLayer.value.width, 
    draggedLayer.value.height,
    draggedLayer.value.id
  )
  newX = snapResult.x
  newY = snapResult.y
  activeSnapGuides.value = snapResult.guides

  pendingDragUpdate = { x: newX, y: newY }

  if (dragRafId !== null) {
    cancelAnimationFrame(dragRafId)
  }

  dragRafId = requestAnimationFrame(() => {
    if (draggedLayer.value && pendingDragUpdate) {
      emit('updateLayer', {
        ...draggedLayer.value,
        x: pendingDragUpdate.x,
        y: pendingDragUpdate.y
      }, { commit: false, deferCommit: true })
    }
    dragRafId = null
  })
}

const endDrag = () => {
  if (dragRafId !== null) {
    cancelAnimationFrame(dragRafId)
    dragRafId = null
  }

  if (draggedLayer.value && pendingDragUpdate) {
    emit('updateLayer', {
      ...draggedLayer.value,
      x: pendingDragUpdate.x,
      y: pendingDragUpdate.y
    }, { commit: true })
  }

  // Clear snap guides
  activeSnapGuides.value = []
  
  pendingDragUpdate = null
  dragging.value = false
  draggedLayer.value = null
  document.removeEventListener('mousemove', handleDragMove)
  document.removeEventListener('mouseup', endDrag)
}

// Resize handlers
const startResize = (e: MouseEvent, layer: DisplayLayer, handle: string) => {
  // Prevent resizing locked layers
  if (layer.locked) {
    return
  }
  
  resizing.value = true
  draggedLayer.value = layer

  startPos = { x: e.clientX, y: e.clientY }
  startSize = { width: layer.width, height: layer.height }

  // Calculate original aspect ratio for fixed-aspect mode
  const aspectRatio = startSize.width / startSize.height
  const maintainAspectRatio = layer.objectFit === 'fixed-aspect'

  let resizeRafId: number | null = null
  let pendingResizeUpdate: DisplayLayer | null = null

  const handleResizeMove = (moveEvent: MouseEvent) => {
    if (!draggedLayer.value) return

    const deltaX = Math.round((moveEvent.clientX - startPos.x) / totalScale.value)
    const deltaY = Math.round((moveEvent.clientY - startPos.y) / totalScale.value)

    let newWidth = startSize.width
    let newHeight = startSize.height
    let newX = draggedLayer.value.x
    let newY = draggedLayer.value.y

    switch (handle) {
      case 'se':
        newWidth = Math.max(10, startSize.width + deltaX)
        if (maintainAspectRatio) {
          newHeight = newWidth / aspectRatio
        } else {
          newHeight = Math.max(10, startSize.height + deltaY)
        }
        break
      case 'sw':
        newWidth = Math.max(10, startSize.width - deltaX)
        if (maintainAspectRatio) {
          newHeight = newWidth / aspectRatio
        } else {
          newHeight = Math.max(10, startSize.height + deltaY)
        }
        newX = draggedLayer.value.x + (startSize.width - newWidth)
        break
      case 'ne':
        newWidth = Math.max(10, startSize.width + deltaX)
        if (maintainAspectRatio) {
          newHeight = newWidth / aspectRatio
          newY = draggedLayer.value.y + (startSize.height - newHeight)
        } else {
          newHeight = Math.max(10, startSize.height - deltaY)
          newY = draggedLayer.value.y + (startSize.height - newHeight)
        }
        break
      case 'nw':
        newWidth = Math.max(10, startSize.width - deltaX)
        if (maintainAspectRatio) {
          newHeight = newWidth / aspectRatio
          newX = draggedLayer.value.x + (startSize.width - newWidth)
          newY = draggedLayer.value.y + (startSize.height - newHeight)
        } else {
          newHeight = Math.max(10, startSize.height - deltaY)
          newX = draggedLayer.value.x + (startSize.width - newWidth)
          newY = draggedLayer.value.y + (startSize.height - newHeight)
        }
        break
    }

    // Apply snapping to the edges being resized
    const guides: { type: 'vertical' | 'horizontal'; position: number }[] = []
    
    // Snap right edge for 'se' and 'ne' handles
    if (handle === 'se' || handle === 'ne') {
      const rightEdge = newX + newWidth
      const snapResult = snapResize('right', rightEdge, draggedLayer.value.id)
      if (snapResult.snapped) {
        const diff = snapResult.value - rightEdge
        newWidth += diff
        guides.push(...snapResult.guides)
      }
    }
    
    // Snap left edge for 'sw' and 'nw' handles
    if (handle === 'sw' || handle === 'nw') {
      const snapResult = snapResize('left', newX, draggedLayer.value.id)
      if (snapResult.snapped) {
        const diff = newX - snapResult.value
        newX = snapResult.value
        newWidth += diff
        guides.push(...snapResult.guides)
      }
    }
    
    // Snap bottom edge for 'se' and 'sw' handles
    if ((handle === 'se' || handle === 'sw') && !maintainAspectRatio) {
      const bottomEdge = newY + newHeight
      const snapResult = snapResize('bottom', bottomEdge, draggedLayer.value.id)
      if (snapResult.snapped) {
        const diff = snapResult.value - bottomEdge
        newHeight += diff
        guides.push(...snapResult.guides)
      }
    }
    
    // Snap top edge for 'ne' and 'nw' handles
    if ((handle === 'ne' || handle === 'nw') && !maintainAspectRatio) {
      const snapResult = snapResize('top', newY, draggedLayer.value.id)
      if (snapResult.snapped) {
        const diff = newY - snapResult.value
        newY = snapResult.value
        newHeight += diff
        guides.push(...snapResult.guides)
      }
    }
    
    activeSnapGuides.value = guides

    pendingResizeUpdate = {
      ...draggedLayer.value,
      x: newX,
      y: newY,
      width: Math.max(10, newWidth),
      height: Math.max(10, newHeight)
    }

    if (resizeRafId !== null) {
      cancelAnimationFrame(resizeRafId)
    }

    resizeRafId = requestAnimationFrame(() => {
      if (pendingResizeUpdate) {
        emit('updateLayer', pendingResizeUpdate, { commit: false, deferCommit: true })
      }
      resizeRafId = null
    })
  }

  const handleResizeEnd = () => {
    if (resizeRafId !== null) {
      cancelAnimationFrame(resizeRafId)
      resizeRafId = null
    }

    if (pendingResizeUpdate) {
      emit('updateLayer', pendingResizeUpdate, { commit: true })
    }

    // Clear snap guides
    activeSnapGuides.value = []

    pendingResizeUpdate = null
    resizing.value = false
    draggedLayer.value = null
    document.removeEventListener('mousemove', handleResizeMove)
    document.removeEventListener('mouseup', handleResizeEnd)
  }

  document.addEventListener('mousemove', handleResizeMove)
  document.addEventListener('mouseup', handleResizeEnd)
  e.preventDefault()
}

// Crop functions
const startCrop = (e: MouseEvent, layer: DisplayLayer, direction: 'top' | 'bottom' | 'left' | 'right') => {
  // Prevent cropping locked layers
  if (layer.locked) {
    return
  }
  
  e.preventDefault()
  emit('selectLayer', layer.id)
  draggedLayer.value = layer
  
  const initialCrop = layer.crop || { top: 0, left: 0, right: 0, bottom: 0 }
  const startPos = { x: e.clientX, y: e.clientY }
  let cropRafId: number | null = null
  let pendingCropUpdate: DisplayLayer | null = null
  
  const handleCropMove = (moveEvent: MouseEvent) => {
    if (!draggedLayer.value) return
    
    const deltaX = (moveEvent.clientX - startPos.x) / totalScale.value
    const deltaY = (moveEvent.clientY - startPos.y) / totalScale.value
    
    const newCrop = { ...initialCrop }
    
    // Convert pixel deltas to percentages
    const deltaXPercent = (deltaX / draggedLayer.value.width) * 100
    const deltaYPercent = (deltaY / draggedLayer.value.height) * 100
    
    switch (direction) {
      case 'top':
        newCrop.top = Math.max(0, Math.min(initialCrop.top + deltaYPercent, 90))
        break
      case 'bottom':
        newCrop.bottom = Math.max(0, Math.min(initialCrop.bottom - deltaYPercent, 90))
        break
      case 'left':
        newCrop.left = Math.max(0, Math.min(initialCrop.left + deltaXPercent, 90))
        break
      case 'right':
        newCrop.right = Math.max(0, Math.min(initialCrop.right - deltaXPercent, 90))
        break
    }

    pendingCropUpdate = {
      ...draggedLayer.value,
      crop: newCrop
    }

    if (cropRafId !== null) {
      cancelAnimationFrame(cropRafId)
    }

    cropRafId = requestAnimationFrame(() => {
      if (pendingCropUpdate) {
        emit('updateLayer', pendingCropUpdate, { commit: false, deferCommit: true })
      }
      cropRafId = null
    })
  }
  
  const handleCropEnd = () => {
    if (cropRafId !== null) {
      cancelAnimationFrame(cropRafId)
      cropRafId = null
    }

    if (pendingCropUpdate) {
      emit('updateLayer', pendingCropUpdate, { commit: true })
    }

    pendingCropUpdate = null
    draggedLayer.value = null
    document.removeEventListener('mousemove', handleCropMove)
    document.removeEventListener('mouseup', handleCropEnd)
  }
  
  document.addEventListener('mousemove', handleCropMove)
  document.addEventListener('mouseup', handleCropEnd)
}

// Keyboard event handler for deleting layers and moving with arrow keys
const handleKeyDown = (e: KeyboardEvent) => {
  // Don't handle if user is typing in an input field
  const target = e.target as HTMLElement
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
    return
  }

  // Arrow key movement
  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
    if (!props.selectedLayerId) return
    
    const layer = props.layers.find(l => l.id === props.selectedLayerId)
    if (!layer || layer.locked || layer.is_inherited) return
    
    e.preventDefault()
    
    const moveAmount = e.shiftKey ? 10 : 1
    let newX = layer.x
    let newY = layer.y
    
    switch (e.key) {
      case 'ArrowUp':
        newY = Math.round(layer.y - moveAmount)
        break
      case 'ArrowDown':
        newY = Math.round(layer.y + moveAmount)
        break
      case 'ArrowLeft':
        newX = Math.round(layer.x - moveAmount)
        break
      case 'ArrowRight':
        newX = Math.round(layer.x + moveAmount)
        break
    }
    
    emit('updateLayer', {
      ...layer,
      x: newX,
      y: newY
    }, { commit: true })
    return
  }

  // Delete and Backspace
  if (e.key === 'Delete' || e.key === 'Backspace') {
    // Delete the selected layer
    if (props.selectedLayerId) {
      // Find the selected layer
      const selectedLayerToDelete = props.layers.find(layer => layer.id === props.selectedLayerId)
      
      // Don't delete inherited layers
      if (selectedLayerToDelete && !selectedLayerToDelete.is_inherited) {
        e.preventDefault() // Prevent browser back navigation on Backspace
        emit('deleteLayer', props.selectedLayerId)
      }
    }
  }
}

// Clipboard paste handler for images
const handlePaste = async (e: ClipboardEvent) => {
  // Don't handle paste if user is typing in an input field
  const target = e.target as HTMLElement
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
    return
  }

  const items = e.clipboardData?.items
  if (!items) return

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    
    // Check if the item is an image
    if (item.type.startsWith('image/')) {
      e.preventDefault()
      
      const file = item.getAsFile()
      if (!file) continue

      try {
        // Upload the image using the image library store
        const { useImageLibraryStore } = await import('@/stores/imageLibrary')
        const imageLibraryStore = useImageLibraryStore()
        
        const uploadedImage = await imageLibraryStore.uploadImage(file)
        
        if (uploadedImage && uploadedImage.url) {
          // Emit event to add the new layer (let parent handle layer creation)
          emit('addLayer', uploadedImage.url, uploadedImage.original_name || 'Pasted Image')
          
          console.log('📋 Image pasted from clipboard:', uploadedImage.original_name)
        }
      } catch (error) {
        console.error('Failed to paste image:', error)
      }
      
      break // Only handle the first image
    }
  }
}

// Alt key handler for disabling snapping
const handleKeyDownForAlt = (e: KeyboardEvent) => {
  if (e.key === 'Alt') {
    isAltPressed.value = true
  }
}

const handleKeyUpForAlt = (e: KeyboardEvent) => {
  if (e.key === 'Alt') {
    isAltPressed.value = false
  }
}

// Add keyboard listener on mount
onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('keydown', handleKeyDownForPan)
  document.addEventListener('keyup', handleKeyUpForPan)
  document.addEventListener('keydown', handleKeyDownForAlt)
  document.addEventListener('keyup', handleKeyUpForAlt)
  document.addEventListener('paste', handlePaste)
})

// Remove keyboard listener on unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
  document.removeEventListener('keydown', handleKeyDownForPan)
  document.removeEventListener('keyup', handleKeyUpForPan)
  document.removeEventListener('keydown', handleKeyDownForAlt)
  document.removeEventListener('keyup', handleKeyUpForAlt)
  document.removeEventListener('paste', handlePaste)
})
</script>

<style scoped>
/* CanvasEditor-specific styles - animations disabled in editor */
* {
  animation: none !important;
}

/* Smooth zoom transitions */
.zoom-transition {
  transition: transform 0.1s ease-out;
}

/* Prevent text selection during pan */
.cursor-grab, .cursor-grabbing {
  user-select: none;
}
</style>